import { motion } from 'framer-motion';
import { BookOpen, Crown, Heart, Users } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const beliefs = [
  {
    title: 'The Godhead',
    description: 'We believe in the Godhead: YAHWEH (the Father), Yeshua/Jesus (the Son and Savior), and the Holy Spirit.',
    icon: Crown
  },
  {
    title: 'Scripture First',
    description: 'We teach biblical passages within their original context, not through the lens of man-made traditions or denominational interpretation.',
    icon: BookOpen
  },
  {
    title: 'Unity in Faith',
    description: 'We reject denominationalism as man-made division. There is one body, one faith, one baptism.',
    icon: Users
  },
  {
    title: 'Love & Service',
    description: 'We demonstrate our faith through love, serving our community, and supporting those in need.',
    icon: Heart
  }
];

export const About = () => {
  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/1x0fdbpc_Lion%20King%201.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">About Us</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Who We <span className="text-gold">Are</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              True Believer is a non-denominational ministry focused on teaching the Word of God in its true, original context—free from man-made traditions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-cinzel text-3xl text-gold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  True Believer was born from a desire to return to the foundations of faith—the pure, unfiltered Word of God. We saw how denominational divisions created walls between believers, and we knew there had to be a better way.
                </p>
                <p>
                  Based in Texas, we've grown into a virtual community that spans across the nation. Through our weekly Prayer & B.I.D.D. (Break It Down Discussions), we study the "Lost Books" of the Bible, diving deep into scripture and exploring its original Hebrew and Greek meanings to understand what God truly intended.
                </p>
                <p>
                  As a 501(c)(3) certified nonprofit, we're committed not just to teaching, but to serving. Our community outreach programs provide food, clothing, mentorship, and hope to those who need it most.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-sm overflow-hidden border border-border gold-glow">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/q3zrx451_3D%20PNG.png"
                  alt="True Believer Logo"
                  className="w-full h-full object-contain p-12 bg-black"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beliefs Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-cinzel text-3xl sm:text-4xl text-gold mb-4">Our Foundational Beliefs</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These core beliefs guide everything we do and teach.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {beliefs.map((belief, index) => (
              <motion.div
                key={belief.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="card-dark group"
                data-testid={`belief-${index}`}
              >
                <div className="flex gap-6">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <belief.icon className="text-gold" size={28} />
                  </div>
                  <div>
                    <h3 className="font-cinzel text-xl text-foreground mb-3">{belief.title}</h3>
                    <p className="text-muted-foreground">{belief.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-cinzel text-3xl text-gold mb-6">Our Vision</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              We envision a future where we purchase land to grow organic crops and raise organic livestock, providing healthier food options to our community. This dream, combined with our ongoing outreach programs, represents our commitment to holistic ministry—nourishing both body and soul.
            </p>
            <div className="inline-flex items-center gap-2 text-gold border border-gold/30 rounded-full px-6 py-3">
              <Crown size={20} />
              <span className="text-sm uppercase tracking-wider">Building the Kingdom Together</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
